import { useQuery } from "@apollo/client/react"
import { useState, useEffect, useMemo, useRef } from "react"
import { useFrame, useThree } from "@react-three/fiber"
import * as THREE from "three"
import { useControls, folder } from "leva"

import { ProductBoxModel } from './ProductBox'
import { GET_PRODUCTS } from './../Queries'
import { hoveredProductStore, isUiOpen, registerProductPosition, productsReadyStore, getAllProductObjects, getProductObject, selectedProductStore } from '../../store/uiStore'
import { SCAFFALI, SCAFFALE_DEFAULT, posizioneSlot, rotazioneCasuale } from '../../config/scaffali'
import { cfgVita } from '../../config/vita'

// Distanza massima (in metri di scena) per interagire con un prodotto
const INTERACT_DISTANCE = 7

// Slider Leva generati dal config scaffali: per ogni scaffale una
// cartella con start X/Z, passo e altezza dei ripiani. I valori del
// config (scaffali.js) sono i default; gli slider servono a calibrare
// in diretta — trovati i valori giusti, fissarli nel config.
const SCHEMA_SCAFFALI = (() => {
  const schema = {
    debugScaffali: false,
    scalaProdotti: { value: 0.8, min: 0.3, max: 2.5, step: 0.05, label: 'scala prodotti' },
    rotCasuale: { value: 0.35, min: 0, max: 1.5, step: 0.05, label: 'rotazione casuale' },
  }
  SCAFFALI.forEach((s) => {
    const p = s.collezione
    const campi = {
      [`${p}_startX`]: { value: s.start[0], min: -11, max: 11, step: 0.05, label: 'start X' },
      [`${p}_startZ`]: { value: s.start[2], min: -11, max: 11, step: 0.05, label: 'start Z' },
      [`${p}_passo`]: { value: s.passo, min: 0.3, max: 3, step: 0.05, label: 'passo' },
    }
    s.livelli.forEach((y, li) => {
      campi[`${p}_liv${li}`] = { value: y, min: 0, max: 5, step: 0.05, label: `ripiano ${li + 1}` }
    })
    schema[p] = folder(campi, { collapsed: true })
  })
  return schema
})()

function useDiscounts() {
  const [sconti, setSconti] = useState([]);
  useEffect(() => {
    fetch('/api/get-discounts').then(res => res.json()).then(data => !data.error && setSconti(data));
  }, []);
  return sconti;
}

// ============================================================
// Raycast dal CENTRO della camera (il mirino): se una scatola
// prodotto è inquadrata e abbastanza vicina → hoveredProductStore.
// Il tasto E (gestito in Hud.jsx) apre la pagina prodotto.
// ============================================================
function ProductRaycaster() {
  const { camera, scene } = useThree()
  const raycaster = useMemo(() => {
    const r = new THREE.Raycaster()
    // oltre la distanza di interazione non serve testare nulla
    r.far = INTERACT_DISTANCE
    return r
  }, [])
  const center = useMemo(() => new THREE.Vector2(0, 0), [])
  const frame = useRef(0)

  useFrame(() => {
    if (isUiOpen()) return // niente hover con una finestra aperta

    // Il raycast su tutta la scena (modello negozio incluso) è costoso:
    // farlo a ogni frame mangiava FPS e il movimento sembrava più lento
    // rispetto a quando è in pausa (scheda prodotto aperta). Per un
    // hover bastano ~10 controlli al secondo.
    frame.current++
    if (frame.current % 6 !== 0) return

    raycaster.setFromCamera(center, camera)

    // FASE 1 (economica): raggio solo contro le scatole prodotto.
    // Se il mirino non punta nessun prodotto, finisce qui — niente
    // raycast sul modello del negozio (decine di migliaia di triangoli).
    const productHits = raycaster.intersectObjects(getAllProductObjects(), true)
    let found = null

    if (productHits.length > 0 && productHits[0].distance <= INTERACT_DISTANCE) {
      // FASE 2 (solo mentre si punta un prodotto): un raycast di scena
      // per verificare l'occlusione (muri/scaffali davanti al prodotto)
      const hits = raycaster.intersectObjects(scene.children, true)
      const hit = hits.find((h) => {
        if (!h.object.visible) return false
        let obj = h.object
        while (obj) {
          if (obj.userData?.noRay) return false
          obj = obj.parent
        }
        return true
      })
      if (hit && hit.distance <= INTERACT_DISTANCE) {
        let obj = hit.object
        while (obj && !obj.userData?.product) obj = obj.parent
        if (obj) found = obj.userData.product
      }
    }

    if (hoveredProductStore.get()?.id !== found?.id) {
      hoveredProductStore.set(found || null)
    }
  })

  return null
}

export default function ProductsHandler() {
  const sconti = useDiscounts();
  const productsQuery = useQuery(GET_PRODUCTS);

  // Marker di calibrazione + override in diretta del config scaffali
  const controls = useControls('Scaffali', SCHEMA_SCAFFALI, { collapsed: true })
  const { debugScaffali, scalaProdotti, rotCasuale } = controls

  // Respiro delle scatole e reazione al mirino (config/vita.js). Il
  // movimento lo fa ogni scatola per conto suo (ProductBox), ma i
  // cursori stanno qui: ProductsHandler è montato UNA volta sola, e un
  // pannello per prodotto sarebbe una cartella Leva moltiplicata per
  // nove.
  const cfgV = useControls('Vita dei prodotti', {
    attiva: { value: cfgVita.attiva, label: 'accesa' },
    respiro: { value: cfgVita.respiro, min: 0, max: 0.08, step: 0.002, label: 'respiro (su e giù)' },
    dondolio: { value: cfgVita.dondolio, min: 0, max: 0.15, step: 0.005, label: 'dondolio' },
    ritmo: { value: cfgVita.ritmo, min: 0.2, max: 3, step: 0.05, label: 'ritmo' },
    miraAlza: { value: cfgVita.miraAlza, min: 0, max: 0.3, step: 0.005, label: 'nel mirino: si alza' },
    miraIngrandisce: { value: cfgVita.miraIngrandisce, min: 0, max: 0.4, step: 0.01, label: 'nel mirino: si ingrandisce' },
    miraVelocita: { value: cfgVita.miraVelocita, min: 2, max: 20, step: 0.5, label: 'nel mirino: prontezza' },
  }, { collapsed: true })
  // Si applica SOLO quando uno slider si muove davvero, così un valore
  // scritto a mano su window.__vita non dura un frame solo (stessa
  // ragione della cassa).
  const cfgPrec = useRef(null)
  if (!cfgPrec.current || Object.keys(cfgV).some((k) => cfgV[k] !== cfgPrec.current[k])) {
    Object.assign(cfgVita, cfgV)
    cfgPrec.current = { ...cfgV }
  }

  // Avvisa la schermata di caricamento quando la query è conclusa
  // (anche in errore: il negozio si mostra comunque, senza prodotti)
  useEffect(() => {
    if (!productsQuery.loading) productsReadyStore.set(true)
  }, [productsQuery.loading])

  // ARRIVARE DA UN LINK DI PRODOTTO (?prodotto=handle). La vetrina Shopify
  // rimanda qui (vedi shopify/rimbalzo-al-negozio-3d.liquid): chi cercava
  // una scatola di biscotti su Google deve trovarsi davanti QUELLA, non
  // l'ingresso del negozio. L'indirizzo viene poi ripulito, cosi'
  // ricaricando la pagina non si riapre da solo.
  const daLink = useRef(false)
  useEffect(() => {
    if (daLink.current || productsQuery.loading || !productsQuery.data) return undefined
    const handle = new URLSearchParams(window.location.search).get('prodotto')
    if (!handle) return undefined
    const nodo = productsQuery.data.products.edges
      .find((e) => e.node.handle === handle)?.node
    if (!nodo) return undefined
    daLink.current = true
    // le scatole si registrano qualche fotogramma dopo, e l'oggetto
    // registrato porta con se' lo sconto attivo: si aspetta un attimo
    const quando = setTimeout(() => {
      const obj = getProductObject(nodo.id)
      selectedProductStore.set(obj?.userData?.product ||
        { ...nodo, image: nodo.images.edges[0]?.node.url || '' })
      document.exitPointerLock?.()
      const url = new URL(window.location.href)
      url.searchParams.delete('prodotto')
      window.history.replaceState({}, '', url)
    }, 900)
    return () => clearTimeout(quando)
  }, [productsQuery.loading, productsQuery.data])

  // Config effettivo = scaffali.js con sopra i valori degli slider
  const scaffaliAttivi = SCAFFALI.map((s) => ({
    ...s,
    start: [controls[`${s.collezione}_startX`], 0, controls[`${s.collezione}_startZ`]],
    passo: controls[`${s.collezione}_passo`],
    livelli: s.livelli.map((_, li) => controls[`${s.collezione}_liv${li}`]),
  }))

  if (productsQuery.loading || productsQuery.error) return null;

  // Teniamo l'oggetto Shopify completo (opzioni, varianti, immagini):
  // serve alla pagina prodotto. `image` è una comodità per la scatola 3D.
  const products = productsQuery.data.products.edges.map(({ node }) => ({
    ...node,
    image: node.images.edges[0]?.node.url || '',
  }))

  // DISPOSIZIONE: ogni prodotto va sullo scaffale della sua collezione
  // Shopify (config in scaffali.js); senza collezione → SCAFFALE_DEFAULT.
  // Il contatore per scaffale assegna i posti in ordine di arrivo.
  const contatori = new Map()
  const disposti = products.map((product) => {
    const handles = product.collections?.edges?.map((e) => e.node.handle) || []
    const scaffale = scaffaliAttivi.find((s) => handles.includes(s.collezione)) || SCAFFALE_DEFAULT
    const posto = contatori.get(scaffale) || 0
    contatori.set(scaffale, posto + 1)
    return {
      product,
      position: posizioneSlot(scaffale, posto),
      // scarto di yaw deterministico per prodotto: scatole non tutte
      // perfettamente allineate, come su uno scaffale vero
      rotationY: (scaffale.rotazioneY || 0) + rotazioneCasuale(product.id, rotCasuale),
    }
  })

  return <>
    <ProductRaycaster />
    {disposti.map(({ product, position, rotationY }) => {
        // MATCHING sconto ↔ prodotto: per handle di prodotto/collezione
        // (robusto); fallback sul vecchio confronto per titolo se l'API
        // deployata non espone ancora i campi strutturati.
        const collectionHandles = product.collections?.edges?.map((e) => e.node.handle) || []
        const activeDiscount = sconti.find(sconto => {
            if (sconto.everything) return true;
            if (sconto.productHandles?.includes(product.handle)) return true;
            if (sconto.collectionHandles?.some((h) => collectionHandles.includes(h))) return true;
            if (sconto.productHandles || sconto.collectionHandles) return false;
            // --- fallback legacy (API vecchia): match per titolo ---
            if (!sconto.appliesTo) return false;
            return sconto.appliesTo.includes(product.title.toLowerCase()) ||
                   sconto.appliesTo.includes("tutto il negozio");
        });

        // registra la posizione per il flusso "prodotto → cassa" (CartFlux)
        registerProductPosition(product.id, position)

        return (
          <ProductBoxModel
            key={product.id}
            // activeDiscount viaggia col prodotto: la scheda (ProductPage)
            // lo usa per mostrare il prezzo scontato
            product={{ ...product, activeDiscount: activeDiscount || null }}
            image={product.image}
            position={position}
            rotationY={rotationY}
            scale={scalaProdotti}
            discountText={activeDiscount ? activeDiscount.valueText : null}
          />
        );
      })}
    {/* Sferette di calibrazione: verdi = scaffali, arancioni = default */}
    {debugScaffali && [...scaffaliAttivi, SCAFFALE_DEFAULT].map((s, si) =>
      Array.from({ length: s.perLivello * s.livelli.length }, (_, i) => (
        <mesh key={`slot-${si}-${i}`} position={posizioneSlot(s, i)}>
          <sphereGeometry args={[0.07, 8, 8]} />
          <meshBasicMaterial color={s === SCAFFALE_DEFAULT ? 'orange' : 'lime'} />
        </mesh>
      ))
    )}
  </>
}
